import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@saucelabs/ds-typography';
import { Toggle } from '@saucelabs/ds-inputs';
import {
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';

import SwitchInfoTooltip from './SwitchInfoTooltip';
import { selectors as groupsSelectors } from '../../group';
import selectors from '../selectors';
import operations from '../operations';

const { APP_STORAGE_KIND } = appStorageConstants;

const Instrumentation: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const settings = useSelector(
    groupsSelectors.currentGroupSettingsSelector,
  ) as appStorageRecords.AppStorageGroupSettingsAndroid;
  const currentGroup = useSelector(groupsSelectors.currentGroupSelector);
  const isLoading = useSelector(selectors.isSettingsLoadingSelector);
  const kind = useSelector(groupsSelectors.currentGroupKindSelector);

  const instrumentationEnabled = settings?.instrumentation_enabled ?? false;

  const toggleInstrumentationEnabled = useCallback((enabled: boolean) => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          instrumentation_enabled: instrumentationEnabled,
        },
        settingsName: 'instrumentation',
        updatedValues: {
          instrumentation_enabled: enabled,
        },
      }));
    }
  }, [currentGroup, dispatch, instrumentationEnabled]);

  const toggleSubSetting = useCallback((settingsKey: string, enabled: boolean) => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          instrumentation: {
            [settingsKey]: settings?.instrumentation?.[settingsKey] ?? false,
          },
        },
        settingsName: settingsKey.replace(/_/g, ' '),
        updatedValues: {
          instrumentation: {
            ...settings.instrumentation,
            [settingsKey]: enabled,
          },
        },
      }));
    }
  }, [currentGroup, dispatch, settings?.instrumentation]);

  // Instrumentation is a Android only feature
  if (kind !== APP_STORAGE_KIND.ANDROID) return null;

  return (
    <>
      <div className="flex justify-between mb-1">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Instrumentation
          </Typography>
          <SwitchInfoTooltip
            title="Allows you to use advanced features when testing your app in the real device cloud, like Image Injection and screenshotting secure views."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-instrumentation-setting-enabled"
          isDisabled={isLoading}
          isSelected={instrumentationEnabled}
          onChange={toggleInstrumentationEnabled}
        />
      </div>

      <div className="flex justify-between w-full mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Network Capture
          </Typography>
          <SwitchInfoTooltip
            title="Enables you to capture network packets during tests to see all HTTP request and responses."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-instrumentation-setting-network_capture"
          isDisabled={isLoading || !instrumentationEnabled}
          isSelected={settings?.instrumentation?.network_capture ?? false}
          onChange={(enabled) => toggleSubSetting('network_capture', enabled)}
        />
      </div>

      <div className="flex justify-between w-full mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Image Injection
          </Typography>
          <SwitchInfoTooltip
            title="Enables you to mimic camera behavior when testing applications by letting you upload an image and present it to the application as if it was read by the device camera."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-instrumentation-setting-image_injection"
          isDisabled={isLoading || !instrumentationEnabled}
          isSelected={settings?.instrumentation?.image_injection ?? false}
          onChange={(enabled) => toggleSubSetting('image_injection', enabled)}
        />
      </div>

      <div className="flex justify-between w-full mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Biometrics Interception
          </Typography>
          <SwitchInfoTooltip
            title="Allows you to choose authentication options if your mobile app requires a biometric authentication, either fingerprint recognition or face recognition."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-instrumentation-setting-biometrics"
          isDisabled={isLoading || !instrumentationEnabled}
          isSelected={settings?.instrumentation?.biometrics ?? false}
          onChange={(enabled) => toggleSubSetting('biometrics', enabled)}
        />
      </div>

      <div className="flex justify-between w-full mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Bypass Screenshot Restriction
          </Typography>
          <SwitchInfoTooltip
            title="Enables you to take screenshots of your app during tests, even if your app does not allow screenshots for security reasons."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-instrumentation-setting-bypass_screenshot_restriction"
          isDisabled={isLoading || !instrumentationEnabled}
          isSelected={settings?.instrumentation?.bypass_screenshot_restriction ?? false}
          onChange={(enabled) => toggleSubSetting('bypass_screenshot_restriction', enabled)}
        />
      </div>
    </>
  );
};

export default Instrumentation;
